import { useState, useEffect, useRef } from 'react';
import {
  Datagrid,
  List,
  Create,
  Edit,
  TabbedForm,
  FormTab,
  TextField,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  NumberInput,
  BooleanInput,
  CheckboxGroupInput,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  Show,
  TabbedShowLayout,
  Tab,
  NumberField,
  BooleanField,
  ReferenceField,
  ArrayField,
  SingleFieldList,
  FunctionField,
  usePermissions,
  useGetList,
  useRecordContext,
  Link,
  useSimpleFormIteratorItem,
} from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import { ArchivedSelect } from '../components/selects';
import { AppBreadcrumbs } from '../components/Breadcrumbs';
import { can, type Permissions, type Event as EventType, type MeasurementDef, type ScoreTable } from '../types';
import { apiFetch } from '../providers/api-rest-provider';
import {
  eventTransform,
  validateMeasurements,
  validateTeamSizeExact,
  validateTeamSizeMin,
  validateTeamSizeMax,
  validateTeamScoring,
  validateTeamLeak,
} from './events-helpers';
import { TimeUnitToggleRow } from '../components/TimeUnitToggleRow';
import { computeCoverageWarnings, hasDemographicScoreFields, sortVersionHistory } from '../utils/scoreTableHelpers';
import { eventScoringReadiness, type EventReadiness } from '../utils/readiness';

const SCORING_MODE_CHOICES = [
  { id: 'measured', name: 'Measured (quantity, time, distance, etc.)' },
  { id: 'pass_fail', name: 'Pass / Fail only' },
];

const MEASUREMENT_KINDS = [
  { id: 'pass_fail', name: 'Pass / Fail' },
  { id: 'time', name: 'Time (compound)' },
  { id: 'distance', name: 'Distance (compound)' },
  { id: 'quantity', name: 'Quantity / Weight' },
];

const AGGREGATION_TYPES = [
  { id: 'best', name: 'Best (highest or fastest)' },
  { id: 'cumulative', name: 'Cumulative (sum of all)' },
  { id: 'average', name: 'Average (mean of all)' },
];

const PARTICIPATION_CHOICES = [
  { id: 'individual', name: 'Individual (default)' },
  { id: 'team', name: 'Team' },
];

const SIZE_MODE_CHOICES = [
  { id: 'exact', name: 'Exact quantity' },
  { id: 'range', name: 'Minimum and maximum' },
];

const TEAM_SCORING_CHOICES = [
  { id: 'single_result', name: 'One result for the whole team' },
  { id: 'aggregate', name: 'Each member records their own result' },
];

const ParticipationSection = () => {
  const participation = useWatch({ name: 'participation' });
  const sizeMode = useWatch({ name: '_team_size_mode' });
  const teamSize = useWatch({ name: 'team_size' });
  const { setValue } = useFormContext();

  // On edit load, derive _team_size_mode from the stored team_size shape.
  const initialized = useWatch({ name: '_team_size_mode' });
  if (!initialized && teamSize && typeof teamSize === 'object') {
    if ('min' in (teamSize as object)) setValue('_team_size_mode', 'range');
    else setValue('_team_size_mode', 'exact');
  }

  return (
    <>
      <RadioButtonGroupInput
        source="participation"
        label="Participation"
        choices={PARTICIPATION_CHOICES}
        defaultValue="individual"
        helperText="Choose how participants are grouped for this event."
      />
      {participation === 'team' && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0 }}>Team Size</Typography>
          <RadioButtonGroupInput
            source="_team_size_mode"
            label="Size rule"
            choices={SIZE_MODE_CHOICES}
            defaultValue="exact"
          />
          {(!sizeMode || sizeMode === 'exact') ? (
            <NumberInput
              fullWidth
              source="team_size.exact"
              label="Exact number of members"
              min={2}
              validate={validateTeamSizeExact}
              helperText="Each team must have exactly this many members."
            />
          ) : (
            <>
              <NumberInput
                fullWidth
                source="team_size.min"
                label="Minimum members"
                min={2}
                validate={validateTeamSizeMin}
                helperText="Teams must have at least this many members."
              />
              <NumberInput
                fullWidth
                source="team_size.max"
                label="Maximum members"
                min={2}
                validate={validateTeamSizeMax}
                helperText="Teams must have at most this many members."
              />
            </>
          )}
          <SelectInput
            fullWidth
            source="team_scoring"
            label="Scoring model"
            choices={TEAM_SCORING_CHOICES}
            validate={validateTeamScoring}
            helperText="'One result' fans out a single measurement to all members. 'Each member' lets each person record their own result."
          />
        </>
      )}
    </>
  );
};

interface UnitSchemeEntry {
  id: string;
  kind: string;
  label: string;
  base_unit: string;
  parts: Array<{ label: string; factor: number; max?: number }>;
}

function useUnitSchemes() {
  const [schemes, setSchemes] = useState<UnitSchemeEntry[]>([]);
  useEffect(() => {
    apiFetch('unit-schemes').then(setSchemes).catch(() => {});
  }, []);
  return schemes;
}

const MeasurementUnitsField = () => {
  const { index } = useSimpleFormIteratorItem();
  const prefix = `measurements.${index}`;
  const kind = useWatch({ name: `${prefix}.kind` });
  const unitSchemeId = useWatch({ name: `${prefix}.unit_scheme_id` }) ?? '';
  const existingScheme = useWatch({ name: `${prefix}.unit_scheme` });
  const { setValue } = useFormContext();
  const allSchemes = useUnitSchemes();

  // Auto-check lower_is_better only when the author CHANGES kind to 'time'
  // during this editing session. Setting it unconditionally on mount would
  // silently flip a stored `false` (e.g. a plank hold, where a LONGER time is
  // better) back to `true` every time the record is reopened for editing.
  const prevKindRef = useRef(kind);
  useEffect(() => {
    if (kind === 'time' && prevKindRef.current !== 'time') {
      setValue(`${prefix}.lower_is_better`, true);
    }
    prevKindRef.current = kind;
  }, [kind, prefix, setValue]);

  if (kind === 'pass_fail') return null;

  if (existingScheme && !unitSchemeId) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Legacy scheme — run the units migration
      </Typography>
    );
  }

  // Task 6: time kind uses toggle row instead of dropdown
  if (kind === 'time') {
    return (
      <TimeUnitToggleRow
        value={unitSchemeId}
        onChange={(id) => setValue(`${prefix}.unit_scheme_id`, id)}
      />
    );
  }

  const filtered = allSchemes.filter(s => s.kind === kind);
  const selected = allSchemes.find(s => s.id === unitSchemeId);

  return (
    <Box sx={{ width: '100%' }}>
      <SelectInput
        fullWidth
        label="Units"
        source="unit_scheme_id"
        choices={filtered.map(s => ({ id: s.id, name: s.label }))}
      />
      {selected && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {selected.label} — stored as {selected.base_unit}
        </Typography>
      )}
    </Box>
  );
};

const MeasurementsSection = () => {
  const mode = useWatch({ name: 'scoring_mode' });
  if (mode === 'pass_fail') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No measurements are recorded for pass/fail events.
      </Typography>
    );
  }
  return (
    <ArrayInput
      label="Measurements"
      source="measurements"
      validate={validateMeasurements}
      helperText="Define one or more measurements for this event. Each measurement has its own unit scheme and aggregation rule."
    >
      <SimpleFormIterator>
        <SelectInput
          fullWidth
          label="Kind"
          source="kind"
          defaultValue="quantity"
          choices={MEASUREMENT_KINDS}
        />
        <SelectInput
          fullWidth
          label="Aggregation (for multiple attempts)"
          source="aggregation"
          defaultValue="best"
          choices={AGGREGATION_TYPES}
        />
        <BooleanInput
          fullWidth
          label="Lower value is better (e.g. for time-based events)"
          source="lower_is_better"
          defaultValue={false}
        />
        <MeasurementUnitsField />
      </SimpleFormIterator>
    </ArrayInput>
  );
};

const WEARABLE_CATEGORIES = [
  { id: 'effort', name: 'Effort vitals (HR avg/max/zones)' },
  { id: 'readiness', name: 'Recovery/readiness (HRV, sleep, readiness score)' },
  { id: 'strain', name: 'Strain/load trends (rolling training load)' },
];

const WearablesSection = () => {
  const [tenantEnabled, setTenantEnabled] = useState(false);
  useEffect(() => {
    apiFetch('tenants/self')
      .then((s: any) => {
        setTenantEnabled(s?.settings?.wearables?.enabled === true);
      })
      .catch(() => {});
  }, []);

  const wearablesEnabled = useWatch({ name: 'wearables.enabled' });

  if (!tenantEnabled) return null;
  return (
    <>
      <BooleanInput
        source="wearables.enabled"
        label="Include wearable context"
        helperText="Shown alongside results for students who have granted consent. Does not affect scoring."
      />
      {wearablesEnabled && (
        <CheckboxGroupInput
          source="wearables.categories"
          label="Metric categories"
          choices={WEARABLE_CATEGORIES}
        />
      )}
    </>
  );
};

const EventList = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <List
      exporter={false}
      filters={[<ArchivedSelect key="archived" />]}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid
        rowClick={can(permissions, 'manage_events') ? 'edit' : 'show'}
      >
        <TextField label="Event Name" source="name" />
        <NumberField label="Max Attempts" source="max_attempts" />
        <FunctionField
          label="Measurements"
          render={(record: EventType) => {
            const count = record.measurements?.length;
            if (count) return `${count} measurement${count > 1 ? 's' : ''}`;
            // Fall back to legacy field
            return record.scoring_type ?? '—';
          }}
        />
      </Datagrid>
    </List>
  );
};

// ---------------------------------------------------------------------------
// Local chips used in the Event hub
// ---------------------------------------------------------------------------

const ReadinessChip = ({ readiness }: { readiness: EventReadiness }) => {
  if (readiness === 'complete') return <Chip size="small" color="success" label="Scoring complete" />;
  if (readiness === 'needs_table') return <Chip size="small" color="warning" label="Needs scoring table" />;
  return <Chip size="small" color="default" label="Pass-Fail — no table needed" />;
};

const StatusChipLocal = ({ status }: { status?: string }) => {
  if (!status || status === 'active') return <Chip size="small" color="success" label="Active" />;
  if (status === 'superseded') return <Chip size="small" color="default" label="Superseded" />;
  if (status === 'pending_approval') return <Chip size="small" color="warning" label="Pending Approval" />;
  return <Chip size="small" color="error" label={status} />;
};

// ---------------------------------------------------------------------------
// EventScoringTab
// ---------------------------------------------------------------------------

const EventScoringTab = ({
  scoreTables,
  permissions,
}: {
  scoreTables: ScoreTable[];
  permissions: Permissions | undefined;
}) => {
  const record = useRecordContext<EventType>();
  if (!record) return null;

  const isMeasured =
    record.scoring_mode !== 'pass_fail' && (record.measurements?.length ?? 0) > 0;
  const canWrite = can(permissions, 'manage_events');

  if (!isMeasured) {
    return (
      <Box mt={2}>
        <Alert severity="info">
          This is a Pass/Fail event — no scoring table is needed. Results are recorded as Pass or
          Fail only.
        </Alert>
      </Box>
    );
  }

  const measurements: MeasurementDef[] = record.measurements ?? [];

  return (
    <Box mt={2}>
      {measurements.map((m) => {
        const mTables = scoreTables.filter((st) => st.measurement_id === m.measurement_id);
        const historicalDemographicTable = mTables.find((st) => hasDemographicScoreFields(st.rows));
        const activeTable = mTables.find((st) => st.status === 'active' && !hasDemographicScoreFields(st.rows));
        const warnings = activeTable?.rows ? computeCoverageWarnings(activeTable.rows) : [];

        return (
          <Box key={m.measurement_id} mb={3}>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {m.name}
              </Typography>
              {activeTable ? (
                <Chip size="small" color="success" label="Scored" />
              ) : (
                <Chip size="small" color="warning" label="No active table" />
              )}
            </Stack>

            {activeTable ? (
              <Box>
                <Stack direction="row" spacing={4} mb={1} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Label: {activeTable.label ?? '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Effective: {activeTable.effective_date ?? '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Version: {activeTable.version?.slice(0, 10) ?? '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rows: {activeTable.rows?.length ?? 0}
                  </Typography>
                  <Link to={`/score_tables/${activeTable.id}/show`}>
                    <Typography variant="body2">View full table →</Typography>
                  </Link>
                </Stack>

                {/* Read-only mini bands grid (first 10 rows) */}
                {(activeTable.rows?.length ?? 0) > 0 && (
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Result Min</TableCell>
                          <TableCell>Result Max</TableCell>
                          <TableCell>Points</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(activeTable.rows ?? []).slice(0, 10).map((row, i) => (
                          <TableRow key={i}>
                            <TableCell>{row.result_min}</TableCell>
                            <TableCell>{row.result_max}</TableCell>
                            <TableCell>{row.points}</TableCell>
                          </TableRow>
                        ))}
                        {(activeTable.rows?.length ?? 0) > 10 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography variant="caption">
                                …{' '}
                                {(activeTable.rows?.length ?? 0) - 10} more rows —{' '}
                                <Link to={`/score_tables/${activeTable.id}/show`}>view all</Link>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Coverage warnings */}
                {warnings.map((w, i) => (
                  <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                    {w}
                  </Alert>
                ))}

                {/* New version action */}
                {canWrite && (
                  <Link to={`/score_tables/${activeTable.id}/edit`}>
                    <Chip size="small" label="New version" variant="outlined" clickable />
                  </Link>
                )}
              </Box>
            ) : (
              <Box>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No active scoring table for this measurement. Attempts will be recorded without
                  points.
                </Alert>
                {canWrite && (
                  <Link
                    to={`/score_tables/create?event_id=${record.id}&measurement_id=${m.measurement_id}`}
                  >
                    <Chip size="small" label="Create scoring table" color="primary" clickable />
                  </Link>
                )}
              </Box>
            )}
            {historicalDemographicTable && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Historical demographic-scored table{' '}
                <Link to={`/score_tables/${historicalDemographicTable.id}/show`}>available as reconstruction evidence</Link>.
                It is ineligible for active scoring.
              </Alert>
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// EventHistoryTab
// ---------------------------------------------------------------------------

const EventHistoryTab = ({ scoreTables }: { scoreTables: ScoreTable[] }) => {
  const record = useRecordContext<EventType>();
  if (!record) return null;

  const measurements: MeasurementDef[] = record.measurements ?? [];
  if (measurements.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No measurements on this event.
      </Typography>
    );
  }

  return (
    <Box mt={2}>
      {measurements.map((m) => {
        const mTables = sortVersionHistory(
          scoreTables.filter((st) => st.measurement_id === m.measurement_id),
        ) as ScoreTable[];
        return (
          <Box key={m.measurement_id} mb={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {m.name}
            </Typography>
            {mTables.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No scoring tables for this measurement.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Rows</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mTables.map((st) => (
                    <TableRow key={st.id}>
                      <TableCell>{st.effective_date ?? '—'}</TableCell>
                      <TableCell>{st.version?.slice(0, 10) ?? '—'}</TableCell>
                      <TableCell>
                        <StatusChipLocal
                          status={
                            hasDemographicScoreFields(st.rows)
                              ? 'Historical — ineligible'
                              : st.status
                          }
                        />
                      </TableCell>
                      <TableCell>{st.rows?.length ?? 0}</TableCell>
                      <TableCell>{st.label ?? '—'}</TableCell>
                      <TableCell>
                        <Link to={`/score_tables/${st.id}/show`}>View</Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// EventHubLayout — rendered inside <Show>
// ---------------------------------------------------------------------------

const EventHubLayout = () => {
  const record = useRecordContext<EventType>();
  const { permissions } = usePermissions<Permissions>();
  const { data: scoreTables = [] } = useGetList<ScoreTable>('score_tables', {
    filter: { event_id: record?.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'effective_date', order: 'DESC' },
  });

  const readiness: EventReadiness = record
    ? eventScoringReadiness(record, scoreTables)
    : 'no_table_needed';

  return (
    <Box p={2}>
      <AppBreadcrumbs
        items={[{ label: 'Events', to: '/events' }, { label: record?.name ?? 'Event' }]}
      />

      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Typography variant="h5">{record?.name}</Typography>
        <ReadinessChip readiness={readiness} />
      </Stack>

      <TabbedShowLayout>
        <Tab label="Overview">
          <TextField source="name" />
          <NumberField label="Maximum # of Attempts" source="max_attempts" />
          <BooleanField label="Grade Entire Team" source="grade_entire_team" />
          <ReferenceField label="Alternate Event" source="fallback_event_id" reference="events">
            <TextField source="name" />
          </ReferenceField>
          <ArrayField label="Event Observed Behaviors" source="behaviors">
            <SingleFieldList>
              <TextField source="name" />
            </SingleFieldList>
          </ArrayField>
        </Tab>

        <Tab label="Measurements">
          <ArrayField label="Measurements" source="measurements">
            <Datagrid bulkActionButtons={false}>
              <TextField source="name" label="Name" />
              <TextField source="kind" label="Kind" />
              <TextField source="aggregation" label="Aggregation" />
              <FunctionField
                label="Unit Scheme"
                render={(m: any) =>
                  m?.unit_scheme?.parts?.map((p: any) => p.label).join(' / ') ||
                  m?.unit_scheme?.base_unit ||
                  '—'
                }
              />
            </Datagrid>
          </ArrayField>
          {/* Legacy fallback */}
          <TextField label="Legacy Scoring Type" source="scoring_type" />
          <TextField label="Legacy Unit" source="unit" />
        </Tab>

        <Tab label="Scoring">
          <EventScoringTab scoreTables={scoreTables} permissions={permissions} />
        </Tab>

        <Tab label="History">
          <EventHistoryTab scoreTables={scoreTables} />
        </Tab>
      </TabbedShowLayout>
    </Box>
  );
};

const EventShow = () => (
  <Show>
    <EventHubLayout />
  </Show>
);

const EventCreate = () => (
  <Create transform={eventTransform}>
    <EventForm />
  </Create>
);

const EventEdit = () => (
  <Edit mutationMode="pessimistic" transform={eventTransform}>
    <EventForm />
  </Edit>
);

const EventForm = () => (
  <TabbedForm
    toolbar={<ArchivalToolbar />}
    validate={validateTeamLeak}
    warnWhenUnsavedChanges
  >
    <FormTab label="Details">
      <TextInput fullWidth label="Event Name" source="name" />
      <SelectInput
        fullWidth
        label="Scoring Mode"
        source="scoring_mode"
        choices={SCORING_MODE_CHOICES}
        defaultValue="measured"
        helperText="Pass/Fail hides the Measurements tab and records only a pass or fail result."
      />
      <NumberInput fullWidth label="Maximum # of Attempts" source="max_attempts" min={0} />
      {/* grade_entire_team: stored for future team scoring use — no scoring behavior is triggered today */}
      <BooleanInput fullWidth label="Grade Entire Team" defaultValue={false} source="grade_entire_team" />
      <ParticipationSection />
      <WearablesSection />
      <ReferenceInput perPage={100} label="Alternate Event" source="fallback_event_id" reference="events">
        <SelectInput optionText="name" />
      </ReferenceInput>
      <ArrayInput label="Event Observed Behaviors" source="behaviors">
        <SimpleFormIterator>
          <TextInput fullWidth label="Behavior" source="name" />
        </SimpleFormIterator>
      </ArrayInput>
    </FormTab>

    <FormTab label="Measurements">
      <MeasurementsSection />
    </FormTab>
  </TabbedForm>
);

export const events = {
  list: EventList,
  show: EventShow,
  create: EventCreate,
  edit: EventEdit,
};
